/**
 * useProducts Hook
 * React Query hooks for product operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getProducts,
  getProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  getAffaireProducts,
  addAffaireProduct,
  updateAffaireProduct,
  removeAffaireProduct,
  getAffaireBillingSummary,
  getAffaireBillingContact,
  getAffaireBillingContacts
} from '../api/facturation.api';
import type { Product, CreateProductData, AddAffaireProductData } from '../types/facturation.types';
import { invoiceKeys } from './useInvoices';
import { tarifKeys } from './useTarifs';

// Retry configuration for mutations with exponential backoff
// Only retry on network errors or 5xx server errors, not on 4xx client errors
const MUTATION_RETRY_CONFIG = {
  retry: (failureCount: number, error: any) => {
    // Don't retry on 4xx client errors (business logic errors)
    const status = error?.response?.status;
    if (status && status >= 400 && status < 500) {
      return false;
    }
    // Retry up to 3 times for network errors or 5xx server errors
    return failureCount < 3;
  },
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // 1s, 2s, 4s... max 30s
};

// Query keys
export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (activeOnly: boolean) => [...productKeys.lists(), { activeOnly }] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: number) => [...productKeys.details(), id] as const,
  affaireProducts: (affaireId: string) => [...productKeys.all, 'affaire', affaireId] as const,
  billingSummary: (affaireId: string) => [...productKeys.all, 'summary', affaireId] as const,
  billingContact: (affaireId: string) => [...productKeys.all, 'contact', affaireId] as const,
  billingContacts: (affaireId: string) => [...productKeys.all, 'contacts', affaireId] as const,
};

// Get all products from catalog
export const useProducts = (activeOnly = true) => {
  return useQuery({
    queryKey: productKeys.list(activeOnly),
    queryFn: () => getProducts(activeOnly),
    staleTime: 5 * 60 * 1000, // 5 minutes - product catalog doesn't change often
    refetchOnWindowFocus: true,
  });
};

// Get single product by ID
export const useProduct = (id: number) => {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => getProductById(id),
    enabled: !!id,
  });
};

// Get products assigned to an affaire
export const useAffaireProducts = (affaireId: string) => {
  return useQuery({
    queryKey: productKeys.affaireProducts(affaireId),
    queryFn: () => getAffaireProducts(affaireId),
    enabled: !!affaireId,
    staleTime: 0, // Always refetch on mount for fresh affaire data
  });
};

// Get billing summary for an affaire
export const useBillingSummary = (affaireId: string) => {
  return useQuery({
    queryKey: productKeys.billingSummary(affaireId),
    queryFn: () => getAffaireBillingSummary(affaireId),
    enabled: !!affaireId,
    staleTime: 0, // Always refetch on mount for fresh amounts
  });
};

// Get billing contact for an affaire (returns first one)
export const useBillingContact = (affaireId: string) => {
  return useQuery({
    queryKey: productKeys.billingContact(affaireId),
    queryFn: () => getAffaireBillingContact(affaireId),
    enabled: !!affaireId,
    staleTime: 0, // Always refetch on mount for fresh contact data
  });
};

// Get ALL billing contacts for an affaire (for selection when multiple FACTURE contacts exist)
export const useBillingContacts = (affaireId: string) => {
  return useQuery({
    queryKey: productKeys.billingContacts(affaireId),
    queryFn: () => getAffaireBillingContacts(affaireId),
    enabled: !!affaireId,
    staleTime: 0, // Always refetch on mount for fresh contact data
    retry: false, // Don't retry - NO_FACTURATION_CONTACT is a business error, not a temporary failure
  });
};

// Create product mutation
export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProductData) => createProduct(data),
    ...MUTATION_RETRY_CONFIG,
    // Optimistic update
    onMutate: async (newProductData) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: productKeys.lists() });

      // Snapshot previous value
      const previousProducts = queryClient.getQueryData(productKeys.list(true));

      // Optimistically add new product (with temporary ID)
      queryClient.setQueryData<Product[]>(productKeys.list(true), (old) => {
        if (!old) return old;

        const optimisticProduct: Product = {
          id: -Date.now(), // Temporary negative ID
          product_code: newProductData.productCode,
          description: newProductData.description,
          unit_price_htva: newProductData.unitPriceHtva,
          tva_rate: newProductData.tvaRate ?? 21,
          is_active: newProductData.isActive ?? true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        return [...old, optimisticProduct];
      });

      // Return context with snapshot
      return { previousProducts };
    },
    // Rollback on error
    onError: (err, newProductData, context) => {
      if (context?.previousProducts) {
        queryClient.setQueryData(productKeys.list(true), context.previousProducts);
      }
    },
    // Refetch after success or error
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
};

// Update product mutation
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateProductData> }) =>
      updateProduct(id, data),
    ...MUTATION_RETRY_CONFIG,
    // Optimistic update
    onMutate: async ({ id, data: updatedData }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: productKeys.lists() });
      await queryClient.cancelQueries({ queryKey: productKeys.detail(id) });

      // Snapshot previous values
      const previousProducts = queryClient.getQueryData(productKeys.list(true));
      const previousProductsInactive = queryClient.getQueryData(productKeys.list(false));
      const previousProduct = queryClient.getQueryData(productKeys.detail(id));

      // Optimistically update the product in lists
      const updateProductInList = (old: Product[] | undefined): Product[] | undefined => {
        if (!old) return old;

        return old.map((p) =>
          p.id === id
            ? {
                ...p,
                product_code: updatedData.productCode ?? p.product_code,
                description: updatedData.description ?? p.description,
                unit_price_htva: updatedData.unitPriceHtva ?? p.unit_price_htva,
                tva_rate: updatedData.tvaRate ?? p.tva_rate,
                is_active: updatedData.isActive ?? p.is_active,
                updated_at: new Date().toISOString(),
              }
            : p
        );
      };

      queryClient.setQueryData<Product[]>(productKeys.list(true), updateProductInList);
      queryClient.setQueryData<Product[]>(productKeys.list(false), updateProductInList);

      // Optimistically update the detail query
      queryClient.setQueryData<Product>(productKeys.detail(id), (old) => {
        if (!old) return old;

        return {
          ...old,
          product_code: updatedData.productCode ?? old.product_code,
          description: updatedData.description ?? old.description,
          unit_price_htva: updatedData.unitPriceHtva ?? old.unit_price_htva,
          tva_rate: updatedData.tvaRate ?? old.tva_rate,
          is_active: updatedData.isActive ?? old.is_active,
          updated_at: new Date().toISOString(),
        };
      });

      // Return context with snapshots
      return { previousProducts, previousProductsInactive, previousProduct };
    },
    // Rollback on error
    onError: (err, { id }, context) => {
      if (context?.previousProducts) {
        queryClient.setQueryData(productKeys.list(true), context.previousProducts);
      }
      if (context?.previousProductsInactive) {
        queryClient.setQueryData(productKeys.list(false), context.previousProductsInactive);
      }
      if (context?.previousProduct) {
        queryClient.setQueryData(productKeys.detail(id), context.previousProduct);
      }
    },
    // Refetch after success or error
    onSettled: (_, __, { id }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
};

// Delete product mutation
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteProduct(id),
    ...MUTATION_RETRY_CONFIG,
    // Optimistic update
    onMutate: async (deletedId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: productKeys.lists() });

      // Snapshot previous values
      const previousProducts = queryClient.getQueryData(productKeys.list(true));
      const previousProductsInactive = queryClient.getQueryData(productKeys.list(false));

      // Optimistically remove the product from lists
      const removeProductFromList = (old: Product[] | undefined): Product[] | undefined => {
        if (!old) return old;
        return old.filter((p) => p.id !== deletedId);
      };

      queryClient.setQueryData<Product[]>(productKeys.list(true), removeProductFromList);
      queryClient.setQueryData<Product[]>(productKeys.list(false), removeProductFromList);

      // Return context with snapshots
      return { previousProducts, previousProductsInactive };
    },
    // Rollback on error
    onError: (err, deletedId, context) => {
      if (context?.previousProducts) {
        queryClient.setQueryData(productKeys.list(true), context.previousProducts);
      }
      if (context?.previousProductsInactive) {
        queryClient.setQueryData(productKeys.list(false), context.previousProductsInactive);
      }
    },
    // Refetch after success or error
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
};

// Add product to affaire mutation
export const useAddAffaireProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ affaireId, data }: { affaireId: string; data: AddAffaireProductData }) =>
      addAffaireProduct(affaireId, data),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, { affaireId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.affaireProducts(affaireId) });
      queryClient.invalidateQueries({ queryKey: productKeys.billingSummary(affaireId) });
      // Also invalidate invoice eligibility so the invoice modal sees the new product
      queryClient.invalidateQueries({ queryKey: invoiceKeys.eligibility(affaireId) });
      // Invalidate affaire context to update remembered banque/service selection
      queryClient.invalidateQueries({ queryKey: tarifKeys.affaireContext(affaireId) });
    },
  });
};

// Update affaire product mutation
export const useUpdateAffaireProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ affaireId, productId, data }: { affaireId: string; productId: number; data: Partial<AddAffaireProductData> }) =>
      updateAffaireProduct(affaireId, productId, data),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, { affaireId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.affaireProducts(affaireId) });
      queryClient.invalidateQueries({ queryKey: productKeys.billingSummary(affaireId) });
      // Also invalidate invoice eligibility so the invoice modal sees the updated product
      queryClient.invalidateQueries({ queryKey: invoiceKeys.eligibility(affaireId) });
    },
  });
};

// Remove product from affaire mutation
export const useRemoveAffaireProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ affaireId, productId }: { affaireId: string; productId: number }) =>
      removeAffaireProduct(affaireId, productId),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, { affaireId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.affaireProducts(affaireId) });
      queryClient.invalidateQueries({ queryKey: productKeys.billingSummary(affaireId) });
      // Also invalidate invoice eligibility so the invoice modal sees the removal
      queryClient.invalidateQueries({ queryKey: invoiceKeys.eligibility(affaireId) });
    },
  });
};
